import StatWithIndicator from '../../stories/StatWithIndicator/StatWithIndicator'
import { DataPoint, useMetrics } from '../api'
import { useWidgetContext } from '../providers'
import { WidgetWrapper } from '../WidgetWrapper'
import { useFmt, useFmtColor } from '../format'
import { z } from 'zod'
import { useWidgetEditor } from '../state'
import { WidgetForm } from '../WidgetForm'
import { ProgressBoxOptions } from '../schemas'
import Stat from '../../stories/Stat/Stat'

type OptionsType = z.infer<typeof ProgressBoxOptions>

export default function ProgressBox(props: { id: string }) {
  const ctx = useWidgetContext()
  const editor = useWidgetEditor()
  const config = editor.useConfig(props.id)
  const metrics = useMetrics()

  if (config != null && config.widget !== 'ProgressBox') {
    console.error(
      `Invalid widget config for ProgressBox widget ${props.id}`,
      config
    )
    return null
  }

  const options: OptionsType =
    config?.options || ProgressBoxRegistry.getInitialOptions()
  const metricKey = options.metric || ctx.metric || ''
  const metric = metrics.data?.find((m) => m.key === metricKey)
  const title = options.title || metric?.name || options.metric || ''
  const isPercentMetric = metric?.is_percent_metric

  const component = options.component || ctx.component || 'actual'
  const statistic = options.statistic || ctx.statistic || 'value'

  return (
    <WidgetWrapper
      id={props.id}
      transformData={(data: DataPoint[]) => {
        const value = data && data.length === 1 ? data[0].value : null
        return { value, progressValue: (value || 0) * 100 }
      }}
      render={(data) => {
        const fmtValue = useFmt({
          metric: metricKey,
          component,
          statistic,
          value: data.value,
        })
        const color = useFmtColor({
          metric: metricKey,
          component,
          statistic,
          value: data.value,
        })

        return isPercentMetric ? (
          <Stat title={title} value={fmtValue} background="white" />
        ) : (
          <StatWithIndicator
            title={title}
            value={fmtValue}
            progress={data.progressValue || undefined}
            progressColor={color}
          />
        )
      }}
      query={() => ({
        period_type: ctx.period_type || 'total',
        period_range: false,
        periods: [ctx.period || '2025-01-01'],
        subject_type: ctx.subject_type || 'user',
        subject_ids: [ctx.subject || 0],
        metrics: [metricKey],
        components: [component],
        statistics: [statistic],
        competition_id: ctx.competition_id,
      })}
    />
  )
}

export const ProgressBoxRegistry = {
  component: ProgressBox,
  formComponent: WidgetForm,
  mock: () => <div>Mock ProgressBox</div>,
  getInitialOptions: () => ProgressBoxOptions.parse({}),
}
